import MDBox from 'components/MDBox';
import Fade from '@mui/material/Fade';
import { darken } from '@mui/system';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import { useNotification } from 'hook/useNotification';
import { useCallback, useEffect, useState } from 'react';
import { fErrors } from 'utils/formatError';
import { useIntl } from 'react-intl';
import FileMetadatas from 'pages/fileViewer/components/FileMetadatas';
import { useFindBySpecificationQuery } from 'services/entityApi';
import { useParams } from 'react-router-dom';
import { useQueryData } from 'hook/useQueryData';
import RelatedDocumentsCarousel from 'pages/fileViewer/components/carousel/RelatedDocumentsCarousel';
import { Fab, SwipeableDrawer, useMediaQuery, useTheme } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import PropTypes from 'prop-types';
import * as StringUtils from 'utils/stringUtils';
import FileViewer from './components/viewer/FileViewer';
import useConf from '../../hook/useConf';
import { useGetConfigurationQuery } from '../../services/applicationApi';

/**
 * FileViewerContainer is a component that retrieves and presents file metadata and content.
 * The file to display is based on a hash parameter in the URL. If the hash isn't found, the component will display
 * data based on the file previously stored in the local storage. The file's metadata and content are fetched from
 * server.
 *
 * @component
 * @example
 * <FileViewerContainer />
 *
 * @returns {JSX.Element} The FileViewerContainer component which displays the file's metadata and its content.
 * If the content is still loading, a progress bar is shown instead.
 */
function FileViewerContainer({ onEntityUpdated }) {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();
  const { getValue: conf } = useConf(useGetConfigurationQuery);
  const theme = useTheme();
  const primaryColor = theme?.palette.primary.main;
  const xlWidthUp = useMediaQuery(theme.breakpoints.up('xxl'));
  const [file, setFile] = useState(JSON.parse(localStorage.getItem('fileViewerFile')));
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [contentId, setContentId] = useState(file.contentId);
  const handleRelatedDocumentClick = (newFile) => {
    setContentId(newFile?.contentId);
    setFile(newFile);
  };
  const { hash } = useParams();
  const fileViewerConf = JSON.parse(localStorage.getItem('fileViewerConf')) ?? conf('fileViewer');
  const [isCarouselEmpty, setIsCarouselEmpty] = useState(false);

  const handleCarouselStatus = useCallback((isEmpty) => {
    setIsCarouselEmpty(isEmpty);
  }, []);

  const {
    data: documentData,
    loading,
    error
  } = useQueryData(useFindBySpecificationQuery, {
    viewName: 'fileViewer',
    filter: `hash = ${hash}`,
    size: 1,
    baseType: 'DOCUMENT',
    skip: !hash
  });

  useEffect(() => {
    if (!loading && documentData && documentData.content?.length > 0) {
      const entity = documentData.decode().content[0];
      setContentId(entity.contentId);
      setFile({
        entityId: entity.id,
        hash: entity.hash,
        label: entity.name,
        contentId: entity.contentId
      });
    }
  }, [documentData, loading]);

  useEffect(() => {
    if (error) {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.error.title' }),
        message: fErrors(msg, error)
      });
    }
  }, [addNotification, error, msg]);

  const shouldDisplayViewer = (hash && documentData != null) || !hash;

  return (
    <Fade in timeout={(2000, 500)}>
      <MDBox
        p={2}
        bgColor="container"
        borderRadius="md"
        display="flex"
        flex={1}
        sx={{
          flexDirection: { xs: 'column', xxl: 'row' }
        }}
      >
        {shouldDisplayViewer ? (
          <>
            <MDBox flex={1} display="flex">
              {!isCarouselEmpty || !xlWidthUp ? (
                <>
                  <Tooltip title={msg({ id: 'action.show.properties' })} placement="right">
                    <Fab
                      color="primary"
                      aria-label="open properties"
                      onClick={() => setMetadataOpen(true)}
                      size="medium"
                      sx={{
                        alignSelf: 'center',
                        padding: '0 !important',
                        position: 'absolute',
                        left: '28px',
                        '&:hover': { backgroundColor: darken(primaryColor, 0.2) }
                      }}
                    >
                      <ArrowForwardIosRoundedIcon fontSize="large" />
                    </Fab>
                  </Tooltip>
                  <SwipeableDrawer
                    anchor="left"
                    open={metadataOpen}
                    onClose={() => setMetadataOpen(false)}
                    onOpen={() => setMetadataOpen(true)}
                    sx={{
                      zIndex: '2000',
                      '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: '45%',
                        minWidth: '400px',
                        height: '100%',
                        margin: 0,
                        p: 2
                      }
                    }}
                  >
                    <FileMetadatas
                      entityId={file.entityId}
                      fileViewerConf={fileViewerConf}
                      onEntityUpdated={onEntityUpdated}
                    />
                  </SwipeableDrawer>
                </>
              ) : (
                <MDBox
                  flex={0.35}
                  mr={2}
                  sx={{
                    minHeight: '600px'
                  }}
                >
                  <FileMetadatas
                    entityId={file.entityId}
                    fileViewerConf={fileViewerConf}
                    onEntityUpdated={onEntityUpdated}
                  />
                </MDBox>
              )}
              <MDBox
                flex={1}
                sx={{
                  minHeight: '800px',
                  boxShadow: 'rgba(149, 157, 165, 0.2) 1px -1px 24px 9px'
                }}
              >
                <FileViewer contentId={contentId} fileName={file.label ?? 'no name'} />
              </MDBox>
            </MDBox>
            {StringUtils.toBoolean(fileViewerConf?.relatedDocument?.enabled) &&
              !isCarouselEmpty && (
                <MDBox
                  display="flex"
                  width={{ sx: '100%', xxl: '30%' }}
                  shadow="md"
                  sx={{
                    flexDirection: 'column'
                  }}
                  mt={{ xs: 2, xxl: 0 }}
                  pl={1}
                >
                  <RelatedDocumentsCarousel
                    entityId={file.entityId}
                    onDocumentClick={handleRelatedDocumentClick}
                    conf={fileViewerConf?.relatedDocument}
                    direction={xlWidthUp ? 'column' : 'row'}
                    onCarouselEmpty={handleCarouselStatus}
                  />
                </MDBox>
              )}
          </>
        ) : (
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        )}
      </MDBox>
    </Fade>
  );
}

FileViewerContainer.defaultProps = {
  onEntityUpdated: () => {}
};

FileViewerContainer.propTypes = {
  onEntityUpdated: PropTypes.func // Expected: (entity: object) => void
};

export default FileViewerContainer;
